// Risk Heat Map Component
// Interactive heat map with dropdown selectors for x/y axes and color gradation based on risk impact

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { useLanguage } from '../../state/language/LanguageProvider';
import { useAuth } from '../../state/auth/AuthProvider';
import api from '../../services/api';

interface HeatMapData {
  xValue: string;
  yValue: string;
  riskCount: number;
  highImpactCount: number;
  mediumImpactCount: number;
  lowImpactCount: number;
  normalizedImpact?: number;
}

interface RiskHeatMapProps {
  className?: string;
}

export const RiskHeatMap: React.FC<RiskHeatMapProps> = ({ className }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [xAxis, setXAxis] = useState('business_hierarchy');
  const [yAxis, setYAxis] = useState('functional_hierarchy');
  const [heatMapData, setHeatMapData] = useState<HeatMapData[]>([]);
  const [xAxisValues, setXAxisValues] = useState<string[]>([]);
  const [yAxisValues, setYAxisValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<{x: string, y: string, data: HeatMapData} | null>(null);

  const axisOptions = [
    { value: 'roadmaps', label: t('risk.analytics.heatMap.roadmaps') },
    { value: 'business_hierarchy', label: t('risk.analytics.heatMap.businessHierarchy') },
    { value: 'functional_hierarchy', label: t('risk.analytics.heatMap.functionalHierarchy') },
    { value: 'geographic_hierarchy', label: t('risk.analytics.heatMap.geographicHierarchy') }
  ];

  useEffect(() => {
    loadHeatMapData();
  }, [xAxis, yAxis]);

  const getDimensionValues = async (dimension: string) => {
    try {
      if (!user) return [];
      
      // Build query parameters based on user's scope
      const queryParams = new URLSearchParams({
        tenantId: user.tenantId || '',
        userId: user.id || ''
      });
      
      // Fetch actual values from the selected dimension with user scope filtering
      switch (dimension) {
        case 'roadmaps': {
          // Show all roadmaps - no user scope filtering
          const roadmapsResponse = await api.get(`/roadmap/marketplace/tenant/roadmaps?${queryParams.toString()}`);
          return roadmapsResponse.data?.data?.map((r: any) => r.name) || [];
        }
        case 'business_hierarchy': {
          const businessResponse = await api.get(`/hierarchy/business?${queryParams.toString()}`);
          const businessData = businessResponse.data?.data || [];
          // Get user's business hierarchy position and show children
          const userBusinessHierarchyId = await getUserHierarchyId('business');
          return getChildrenOfUserHierarchy(businessData, userBusinessHierarchyId);
        }
        case 'functional_hierarchy': {
          const functionalResponse = await api.get(`/hierarchy/functional?${queryParams.toString()}`);
          const functionalData = functionalResponse.data?.data || [];
          // Get user's functional hierarchy position and show children
          const userFunctionalHierarchyId = await getUserHierarchyId('functional');
          return getChildrenOfUserHierarchy(functionalData, userFunctionalHierarchyId);
        }
        case 'geographic_hierarchy': {
          const geoResponse = await api.get(`/hierarchy/geographic?${queryParams.toString()}`);
          const geoData = geoResponse.data?.data || [];
          // Get user's geographic hierarchy position and show children
          const userGeographicHierarchyId = await getUserHierarchyId('geographic');
          return getChildrenOfUserHierarchy(geoData, userGeographicHierarchyId);
        }
        default:
          return [];
      }
    } catch (error) {
      console.error(`Error fetching ${dimension} values:`, error);
      return [];
    }
  };


  // Helper function to get children of user's current hierarchy position
  const getChildrenOfUserHierarchy = (hierarchyData: any[], userHierarchyId: string) => {
    if (!hierarchyData || hierarchyData.length === 0 || !userHierarchyId) {
      // If no user hierarchy ID, show all level 1 items as fallback
      return hierarchyData.filter((item: any) => item.level === 1).map((item: any) => item.name);
    }

    // Find the user's current hierarchy item
    const userHierarchyItem = hierarchyData.find((item: any) => item.id === userHierarchyId);
    if (!userHierarchyItem) {
      // If user's hierarchy item not found, show all level 1 items as fallback
      return hierarchyData.filter((item: any) => item.level === 1).map((item: any) => item.name);
    }

    // Find all children of the user's hierarchy position
    const children = hierarchyData.filter((item: any) => item.parent_id === userHierarchyId);
    
    if (children.length === 0) {
      // If no children, show the user's current level items
      return hierarchyData
        .filter((item: any) => item.level === userHierarchyItem.level)
        .map((item: any) => item.name);
    }

    return children.map((item: any) => item.name);
  };

  // Helper function to get user's hierarchy ID from backend
  const getUserHierarchyId = async (hierarchyType: string) => {
    try {
      const response = await api.get(`/users/hierarchy/type/${hierarchyType}`);
      return response.data?.hierarchyId || null;
    } catch (error) {
      console.error(`Error getting user ${hierarchyType} hierarchy ID:`, error);
      return null;
    }
  };

  // Helper function to check if a risk belongs to a hierarchy or its children
  const isRiskInHierarchy = (riskHierarchyId: string | null, targetHierarchyName: string, hierarchyMap: Map<string, any>, allHierarchyData: any[]) => {
    if (!riskHierarchyId) return false;
    
    const riskHierarchy = hierarchyMap.get(riskHierarchyId);
    if (!riskHierarchy) return false;
    
    // Direct match
    if (riskHierarchy.name === targetHierarchyName) return true;
    
    // Check if risk belongs to a child of the target hierarchy
    const targetHierarchy = allHierarchyData.find(h => h.name === targetHierarchyName);
    if (!targetHierarchy) return false;
    
    // Check if risk hierarchy is a descendant of target hierarchy
    return isDescendant(riskHierarchy, targetHierarchy, allHierarchyData);
  };

  // Helper function to check if one hierarchy is a descendant of another
  const isDescendant = (child: any, ancestor: any, allHierarchyData: any[]): boolean => {
    if (child.parent_id === ancestor.id) return true;
    if (!child.parent_id) return false;
    
    const parent = allHierarchyData.find(h => h.id === child.parent_id);
    if (!parent) return false;
    
    return isDescendant(parent, ancestor, allHierarchyData);
  };

  // Helper function to convert severity to numeric score
  const getSeverityScore = (severity: string): number => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 5;
      case 'HIGH': return 4;
      case 'MEDIUM': return 3;
      case 'LOW': return 2;
      case 'MINIMAL': return 1;
      default: return 1;
    }
  };

  // Helper function to convert likelihood to numeric score
  const getLikelihoodScore = (likelihood: string): number => {
    switch (likelihood?.toUpperCase()) {
      case 'CERTAIN': return 5;
      case 'LIKELY': return 4;
      case 'POSSIBLE': return 3;
      case 'UNLIKELY': return 2;
      case 'RARE': return 1;
      default: return 1;
    }
  };

  const loadHeatMapData = async () => {
    setLoading(true);
    try {
      // Fetch actual dimension values
      const [xValues, yValues] = await Promise.all([
        getDimensionValues(xAxis),
        getDimensionValues(yAxis)
      ]);

      // Generate heat map data based on actual values
      const data = await generateHeatMapData(xValues, yValues);
      
      setHeatMapData(data);
      setXAxisValues(xValues);
      setYAxisValues(yValues);
    } catch (error) {
      console.error('Error loading heat map data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateHeatMapData = async (xValues: string[], yValues: string[]) => {
    const data: HeatMapData[] = [];
    
    if (!user) return data;
    
    try {
      // Fetch all active risks for the tenant (not just assigned ones)
      const response = await api.get('/generic/risks');
      
      // Check the actual structure of the response
      let allRisks = [];
      if (Array.isArray(response.data)) {
        allRisks = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        allRisks = response.data.data;
      } else if (response.data && Array.isArray(response.data.risks)) {
        allRisks = response.data.risks;
      } else {
        allRisks = [];
      }
      
      // Filter to only include active risks (exclude closed/archived)
      allRisks = allRisks.filter((risk: any) => {
        const status = risk.status?.toLowerCase();
        return status !== 'closed' && status !== 'archived' && status !== 'resolved';
      });
      
                        console.log('Risk statuses:', [...new Set(allRisks.map((r: any) => r.status))]);
      
      // Fetch hierarchy mappings to match risk hierarchy IDs to names
      const [businessHierarchy, functionalHierarchy, geographicHierarchy] = await Promise.all([
        api.get('/hierarchy/business').catch(() => ({ data: { data: [] } })),
        api.get('/hierarchy/functional').catch(() => ({ data: { data: [] } })),
        api.get('/hierarchy/geographic').catch(() => ({ data: { data: [] } }))
      ]);
      
      // Create lookup maps for hierarchy IDs to names and build hierarchy trees
      const businessMap = new Map();
      const functionalMap = new Map();
      const geographicMap = new Map();
      
      businessHierarchy.data?.data?.forEach((item: any) => {
        businessMap.set(item.id, item);
      });
      functionalHierarchy.data?.data?.forEach((item: any) => {
        functionalMap.set(item.id, item);
      });
      geographicHierarchy.data?.data?.forEach((item: any) => {
        geographicMap.set(item.id, item);
      });
      
      // Calculate impact scores for each cell (severity × likelihood)
      const cellImpacts = new Map();
      
      for (const xVal of xValues) {
        for (const yVal of yValues) {
          const cellKey = `${xVal}|${yVal}`;
          let totalImpact = 0;
          let riskCount = 0;
          
          // Find risks that match this x,y combination
          const matchingRisks = allRisks.filter((risk: any) => {
            let xMatch = false;
            let yMatch = false;
            
            // Match X axis - check if risk belongs to this hierarchy or its children
            if (xAxis === 'business_hierarchy') {
              xMatch = isRiskInHierarchy(risk.business_hierarchy_id, xVal, businessMap, businessHierarchy.data?.data || []);
            } else if (xAxis === 'functional_hierarchy') {
              xMatch = isRiskInHierarchy(risk.functional_hierarchy_id, xVal, functionalMap, functionalHierarchy.data?.data || []);
            } else if (xAxis === 'geographic_hierarchy') {
              xMatch = isRiskInHierarchy(risk.geographic_hierarchy_id, xVal, geographicMap, geographicHierarchy.data?.data || []);
            }
            
            // Match Y axis - check if risk belongs to this hierarchy or its children
            if (yAxis === 'business_hierarchy') {
              yMatch = isRiskInHierarchy(risk.business_hierarchy_id, yVal, businessMap, businessHierarchy.data?.data || []);
            } else if (yAxis === 'functional_hierarchy') {
              yMatch = isRiskInHierarchy(risk.functional_hierarchy_id, yVal, functionalMap, functionalHierarchy.data?.data || []);
            } else if (yAxis === 'geographic_hierarchy') {
              yMatch = isRiskInHierarchy(risk.geographic_hierarchy_id, yVal, geographicMap, geographicHierarchy.data?.data || []);
            }
            
            return xMatch && yMatch;
          });
          
          // Calculate impact for each matching risk (severity × likelihood)
          matchingRisks.forEach((risk: any) => {
            const severityScore = getSeverityScore(risk.severity);
            const likelihoodScore = getLikelihoodScore(risk.likelihood);
            const impact = severityScore * likelihoodScore;
            totalImpact += impact;
            riskCount++;
          });
          
          cellImpacts.set(cellKey, { totalImpact, riskCount });
        }
      }
      
      // Normalize impact scores to 1-10 scale
      const allImpacts = Array.from(cellImpacts.values()).map(cell => cell.totalImpact);
      const minImpact = Math.min(...allImpacts);
      const maxImpact = Math.max(...allImpacts);
      const impactRange = maxImpact - minImpact;
      
      // Generate final heat map data with normalized scores
      for (const xVal of xValues) {
        for (const yVal of yValues) {
          const cellKey = `${xVal}|${yVal}`;
          const cellData = cellImpacts.get(cellKey) || { totalImpact: 0, riskCount: 0 };
          
          // Normalize to 1-10 scale
          let normalizedImpact = 1;
          if (impactRange > 0) {
            normalizedImpact = 1 + ((cellData.totalImpact - minImpact) / impactRange) * 9;
          }
          
          data.push({
            xValue: xVal,
            yValue: yVal,
            riskCount: cellData.riskCount,
            highImpactCount: 0, // Not used in new approach
            mediumImpactCount: 0, // Not used in new approach
            lowImpactCount: 0, // Not used in new approach
            normalizedImpact: Math.round(normalizedImpact * 10) / 10 // Round to 1 decimal
          });
        }
      }
    } catch (error) {
      console.error('Error fetching risks:', error);
      // Fallback to zero data
      for (const xVal of xValues) {
        for (const yVal of yValues) {
          data.push({
            xValue: xVal,
            yValue: yVal,
            riskCount: 0,
            highImpactCount: 0,
            mediumImpactCount: 0,
            lowImpactCount: 0
          });
        }
      }
    }
    
    return data;
  };

  const getHeatMapColor = (data: HeatMapData) => {
    if (data.riskCount === 0) return 'bg-white';
    
    // Use normalizedImpact (1-10 scale) for color gradient
    const impact = data.normalizedImpact || 1;
    
    // Red gradient from pale red (1) to dark red (10)
    if (impact >= 9) {
      return 'bg-red-800'; // Dark red - highest impact
    } else if (impact >= 8) {
      return 'bg-red-700';
    } else if (impact >= 7) {
      return 'bg-red-600';
    } else if (impact >= 6) {
      return 'bg-red-500';
    } else if (impact >= 5) {
      return 'bg-red-400';
    } else if (impact >= 4) {
      return 'bg-red-300';
    } else if (impact >= 3) {
      return 'bg-red-200';
    } else if (impact >= 2) {
      return 'bg-red-100';
    } else {
      return 'bg-red-50'; // Very pale red - lowest impact
    }
  };

  const getCellGradient = (data: HeatMapData) => {
    if (data.riskCount === 0) return undefined;
    
    const impact = data.normalizedImpact || 1;
    
    // Convert Tailwind color names to RGB values
    const getColorValue = (baseColor: string) => {
      const colorMap: { [key: string]: string } = {
        'red-50': '254, 242, 242',
        'red-100': '254, 226, 226',
        'red-200': '254, 202, 202',
        'red-300': '252, 165, 165',
        'red-400': '248, 113, 113',
        'red-500': '239, 68, 68',
        'red-600': '220, 38, 38',
        'red-700': '185, 28, 28',
        'red-800': '153, 27, 27'
      };
      
      return colorMap[baseColor] || '254, 242, 242';
    };
    
    // Determine base color based on impact
    let baseColor = 'red-50';
    if (impact >= 9) baseColor = 'red-800';
    else if (impact >= 8) baseColor = 'red-700';
    else if (impact >= 7) baseColor = 'red-600';
    else if (impact >= 6) baseColor = 'red-500';
    else if (impact >= 5) baseColor = 'red-400';
    else if (impact >= 4) baseColor = 'red-300';
    else if (impact >= 3) baseColor = 'red-200';
    else if (impact >= 2) baseColor = 'red-100';
    
    const rgb = getColorValue(baseColor);
    
    // Create a clean, solid gradient without edge smudging
    return `rgba(${rgb}, 0.8)`;
  };



  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show message if user has no data in their scope
  if (!user) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <p className="text-gray-500">{t('risk.analytics.heatMap.noUserScope')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Always show the heatmap, even with zero data
  // if (xAxisValues.length === 0 || yAxisValues.length === 0) {
  //   return (
  //     <Card className={className}>
  //       <CardHeader>
  //         <CardTitle className="text-lg font-semibold text-gray-900">
  //           {t('risk.analytics.heatMap')}
  //         </CardTitle>
  //         {user && (
  //           <div className="mt-2 text-sm text-gray-600">
  //             <span className="font-medium">{t('risk.analytics.heatMap.scope')}: </span>
  //             <span>{user.tenantName || user.tenantId}</span>
  //           </div>
  //         )}
  //       </CardHeader>
  //       <CardContent className="p-6">
  //         <div className="text-center py-8">
  //           <p className="text-gray-500">{t('risk.analytics.heatMap.noDataInScope')}</p>
  //         </div>
  //       </CardContent>
  //     </Card>
  //   );
  // }

  return (
    <Card className={className}>
      <CardContent className="p-4">
        {/* Heading */}
        <h3 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
          {t('risk.analytics.heatMap.title') || 'Risk Heatmap'}
        </h3>

        {/* Heat Map Grid - Compact, No Margins, Colors Only */}
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Heat Map Rows */}
            {yAxisValues.map((yVal, yIndex) => (
              <div key={yIndex} className="flex" style={{ height: '24px' }}>
                {/* Heat Map Cells */}
                {xAxisValues.map((xVal, xIndex) => {
                  const cellData = heatMapData.find(
                    d => d.xValue === xVal && d.yValue === yVal
                  );
                  
                  if (!cellData) return <div key={xIndex} className="flex-1 h-full bg-white border-0"></div>;
                  
                  return (
                    <div
                      key={xIndex}
                      className={`flex-1 h-full border-0 ${getHeatMapColor(cellData)} cursor-pointer hover:opacity-80 transition-opacity relative overflow-hidden`}
                      onMouseEnter={() => setHoveredCell({x: xVal, y: yVal, data: cellData})}
                      onMouseLeave={() => setHoveredCell(null)}
                      style={{
                        background: cellData.riskCount > 0 ? getCellGradient(cellData) : undefined,
                        borderRadius: '2px'
                      }}
                    >
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Custom Tooltip */}
        {hoveredCell && (
          <div className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-lg pointer-events-none"
               style={{
                 left: '50%',
                 top: '50%',
                 transform: 'translate(-50%, -50%)',
                 minWidth: '200px'
               }}>
            <div className="text-sm font-semibold mb-2">
              {hoveredCell.x} × {hoveredCell.y}
            </div>
            <div className="text-xs">
              {hoveredCell.data.riskCount} risks with aggregate impact score {hoveredCell.data.normalizedImpact?.toFixed(1) || '1.0'}/10
            </div>
          </div>
        )}

        {/* Axis Selectors - Moved to Bottom */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              value={xAxis}
              onChange={(e) => setXAxis(e.target.value)}
              className="w-full"
              options={axisOptions}
            />
          </div>
          <div>
            <Select
              value={yAxis}
              onChange={(e) => setYAxis(e.target.value)}
              className="w-full"
              options={axisOptions}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
